import React, { useState } from 'react';
import { Smartphone, ChevronDown, ArrowRight, ArrowLeft, MessageCircle, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTheme } from '../contexts/ThemeContext';
interface PhoneEntryScreenProps {
  onBack: () => void;
  onContinue: (phone: string, deliveryMethod: 'whatsapp' | 'call') => void;
}
export function PhoneEntryScreen({
  onBack,
  onContinue
}: PhoneEntryScreenProps) {
  const {
    isLight
  } = useTheme();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'whatsapp' | 'call'>('whatsapp');
  const [isLoading, setIsLoading] = useState(false);
  const handleContinue = () => {
    if (!phoneNumber) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onContinue(phoneNumber, deliveryMethod);
    }, 1000);
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-3178" data-mp="3ufg45fjqgzib">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-3179" data-mp="2akvq9nbhst4" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-3180" data-mp="c91s5m50yzmk">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-3181" data-mp="c8il50gwxvnb">
          <ArrowLeft className="w-5 h-5" data-id="element-3182" data-mp="3knsk3zpiakqv" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-8 relative z-10" data-id="element-3183" data-mp="3pgvci4ijro5h">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-3184" data-mp="2s0plz8oqsc15">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${isLight ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'bg-white text-black shadow-lg shadow-white/10'}`} data-id="element-3185" data-mp="2owgrhxr9u6kg">
            <Smartphone className="w-7 h-7" data-id="element-3186" data-mp="2tp1lp0e3aloi" />
          </div>

          <h1 className={`text-3xl font-bold mb-4 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3187" data-mp="2trmlzkyqotk5">
            What's your number?
          </h1>
          <p className={`text-base leading-relaxed mb-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3188" data-mp="2206sr82og8ky">
            {deliveryMethod === 'whatsapp' ? "We'll send a secure verification code to your WhatsApp to set up your AI assistant." : "We'll call you with a secure verification code to set up your AI assistant."}
          </p>

          <div className={`inline-flex items-center gap-1.5 self-start mb-8 px-3 py-1.5 rounded-full border ${deliveryMethod === 'whatsapp' ? isLight ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : isLight ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-blue-500/10 border-blue-500/20 text-blue-300'}`} data-id="element-3189" data-mp="3mdfl9kkttq26">
            {deliveryMethod === 'whatsapp' ? <MessageCircle className="w-3.5 h-3.5" data-id="element-3190" data-mp="1rup1g949s2yw" /> : <Phone className="w-3.5 h-3.5" data-id="element-3191" data-mp="1rwjn5pnd7z41" />}
            <span className="text-xs font-semibold" data-id="element-3192" data-mp="1vuz7fls2atdr">
              {deliveryMethod === 'whatsapp' ? 'Verification via WhatsApp' : 'Verification via phone call'}
            </span>
          </div>

          <div className="w-full space-y-8" data-id="element-3193" data-mp="1n1vz1rw9x6pz">
            <div className="relative" data-id="element-3194" data-mp="2asrle9hyn9pu">
              <label className={`absolute -top-2.5 left-4 px-1.5 text-xs font-medium z-10 transition-colors ${isLight ? 'bg-stone-100 text-slate-500' : 'bg-black text-zinc-500'}`} data-id="element-3195" data-mp="2arq9y3x18l7l">
                Phone Number
              </label>
              <div className={`relative flex items-center gap-3 p-2 rounded-2xl border-2 transition-all duration-300 ${isLight ? 'bg-white border-slate-200 focus-within:border-slate-900 focus-within:shadow-sm' : 'bg-white/5 border-white/10 focus-within:border-white focus-within:bg-white/[0.07]'}`} data-id="element-3196" data-mp="lylxz8u55qkh">
                <div className={`flex items-center justify-center pl-3 pr-2 gap-1.5 shrink-0 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3197" data-mp="19o6ihw5y0vvv">
                  <span className="text-lg leading-none" data-id="element-3198" data-mp="10vl4suh65fdw">🇺🇸</span>
                  <span className="font-semibold tracking-wide" data-id="element-3199" data-mp="vxb5c7oevsmh">+1</span>
                  <ChevronDown className={`w-3.5 h-3.5 opacity-50`} data-id="element-3200" data-mp="vv74jylt79qf" />
                </div>
                <div className={`w-px h-6 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-3201" data-mp="1te041si2eern" />
                <input placeholder="(555) 123-4567" type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className={`flex-1 h-12 bg-transparent outline-none text-lg font-medium tracking-wide placeholder:font-normal ${isLight ? 'text-slate-900 placeholder:text-slate-300' : 'text-white placeholder:text-zinc-600'}`} autoFocus data-id="element-3202" data-mp="1ok4d3a07xs5z" />
              </div>
            </div>

            <div data-id="element-3203" data-mp="2jusrx4vssoai">
              <p className={`text-xs font-semibold uppercase tracking-[0.08em] mb-2.5 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3204" data-mp="2juajl9vv2jm8">
                Send code by
              </p>
              <div className="grid grid-cols-2 gap-2.5" data-id="element-3205" data-mp="2tjkd8u6csacg">
                {[{
                id: 'whatsapp' as const,
                title: 'WhatsApp',
                description: 'Get your code in WhatsApp',
                icon: MessageCircle
              }, {
                id: 'call' as const,
                title: 'Phone call',
                description: 'Hear your code over a call',
                icon: Phone
              }].map(method => {
                const selected = deliveryMethod === method.id;
                const Icon = method.icon;
                return <button key={method.id} type="button" onClick={() => setDeliveryMethod(method.id)} aria-pressed={selected} className={`p-3 rounded-2xl border text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vexu/30 ${selected ? isLight ? 'bg-vexu/5 border-vexu shadow-sm' : 'bg-vexu/10 border-vexu/70' : isLight ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-id="element-3206" data-mp="1m68h8xgljigg">
                      <div className={`w-8 h-8 mb-2 rounded-xl flex items-center justify-center ${selected ? 'bg-vexu text-white' : isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3207" data-mp="27j8sdcwldwqo">
                        <Icon className="w-4 h-4" data-id="element-3208" data-mp="1yo2t6wrtucx4" />
                      </div>
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3209" data-mp="22po6ukc6qv8d">
                        {method.title}
                      </p>
                      <p className={`text-[10px] leading-snug mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3210" data-mp="1tog7dyxxnocp">
                        {method.description}
                      </p>
                    </button>;
              })}
              </div>
            </div>

            <Button fullWidth size="lg" onClick={handleContinue} loading={isLoading} disabled={!phoneNumber} icon={<ArrowRight className="w-5 h-5" data-id="element-3212" data-mp="1wxo758gnv534" />} className="flex-row-reverse" data-id="element-3211" data-mp="3e6xm0uoa713v">
              Continue
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-3213" data-mp="1n7d5xtod39zx">
        {isLoading && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-3214" data-mp="1n6k1izcvkwky">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-3215" data-mp="149sdydqavvb3" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-3216" data-mp="33ld34wvtoljh">
              <div className="relative w-32 h-32 mb-8" data-id="element-3217" data-mp="2yo33koepythx">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-3218" data-mp="3753q1pwv013l" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-3219" data-mp="2o716hqn2ywd6" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-3220" data-mp="cfnfj7vuzu98">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-3221" data-mp="he70p62mp20j">
                    <Smartphone className="w-10 h-10 text-vexu" data-id="element-3222" data-mp="3mwvzpqbgy5l" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3223" data-mp="7o8r2arymbl6">
                {deliveryMethod === 'whatsapp' ? 'Sending Code via WhatsApp' : 'Preparing your verification call'}
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3224" data-mp="3g38wxdk7odet">
                Preparing your verification...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}